import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './ActivitiesList.css';

const SEARCH_STORAGE_KEY = 'activitiesList.searchState';
const SEARCH_TTL_MS = 30 * 60 * 1000;

const loadSavedSearchTerm = () => {
    try {
        const raw = sessionStorage.getItem(SEARCH_STORAGE_KEY);
        if (!raw) return '';

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.term !== 'string' || typeof parsed.savedAt !== 'number') {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
            return '';
        }

        const isExpired = Date.now() - parsed.savedAt > SEARCH_TTL_MS;
        if (isExpired) {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
            return '';
        }

        return parsed.term;
    } catch {
        sessionStorage.removeItem(SEARCH_STORAGE_KEY);
        return '';
    }
};

const getIntensityLabels = (activity) => {
    if (Array.isArray(activity.intensities) && activity.intensities.length > 0) {
        const names = activity.intensities
            .map((intensity) => (typeof intensity === 'string' ? intensity : intensity?.name))
            .filter(Boolean);
        return [...new Set(names)];
    }
    const legacy = activity.level_name || activity.intensityName;
    return legacy ? [legacy] : [];
};

const getCardDescription = (activity) => (
    activity.short_description || activity.description || ''
);

export default function ActivitiesList() {
    const [activities, setActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [searchTerm, setSearchTerm] = useState(loadSavedSearchTerm);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchActivities();
    }, []);

    useEffect(() => {
        filterActivities();
    }, [searchTerm, activities]);

    useEffect(() => {
        const trimmedTerm = searchTerm.trim();
        if (trimmedTerm) {
            sessionStorage.setItem(
                SEARCH_STORAGE_KEY,
                JSON.stringify({ term: trimmedTerm, savedAt: Date.now() })
            );
        } else {
            sessionStorage.removeItem(SEARCH_STORAGE_KEY);
        }
    }, [searchTerm]);

    const filterActivities = () => {
        if (!searchTerm.trim()) {
            setFilteredActivities(activities);
            return;
        }

        const searchLower = searchTerm.toLowerCase();
        const filtered = activities.filter((activity) => (
            activity.name?.toLowerCase().includes(searchLower) ||
            activity.description?.toLowerCase().includes(searchLower) ||
            activity.short_description?.toLowerCase().includes(searchLower) ||
            activity.programName?.toLowerCase().includes(searchLower) ||
            activity.businessName?.toLowerCase().includes(searchLower) ||
            activity.albums?.some((album) => album.name?.toLowerCase().includes(searchLower))
        ));
        setFilteredActivities(filtered);
    };

    const fetchActivities = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities`,
                { withCredentials: true }
            );

            const activityMap = {};

            response.data.forEach((activity) => {
                const activityId = activity.idactivity;

                if (!activityMap[activityId]) {
                    activityMap[activityId] = {
                        ...activity,
                        albums: activity.album_name
                            ? [{ id: activity.album_id, name: activity.album_name }]
                            : [],
                    };
                } else if (activity.album_name) {
                    const exists = activityMap[activityId].albums.some(
                        (album) => album.name === activity.album_name
                    );
                    if (!exists) {
                        activityMap[activityId].albums.push({
                            id: activity.album_id,
                            name: activity.album_name,
                        });
                    }
                }
            });

            const groupedActivities = Object.values(activityMap);
            setActivities(groupedActivities);
            setFilteredActivities(groupedActivities);
            setError('');
        } catch (err) {
            console.error('Error fetching activities:', err);
            setError('Kunne ikke laste aktiviteter');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="act-list-loading">Laster aktiviteter…</div>;
    }

    return (
        <div className="act-list">
            <div className="act-list-container">
                <div className="act-list-top">
                    <Link to="/" className="act-list-back">
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        Tilbake til hovedsiden
                    </Link>
                </div>

                <header className="act-list-header">
                    <h1>Alle aktiviteter</h1>
                    <p>Utforsk øktene våre – finn den som passer deg best.</p>
                </header>

                <div className="act-list-search">
                    <input
                        type="search"
                        placeholder="Søk aktiviteter…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Søk aktiviteter"
                    />
                    <i className="fas fa-search" aria-hidden="true" />
                </div>

                {error && <div className="act-list-error">{error}</div>}

                {searchTerm.trim() && filteredActivities.length > 0 && (
                    <p className="act-list-count">
                        {filteredActivities.length} {filteredActivities.length === 1 ? 'aktivitet' : 'aktiviteter'} funnet
                    </p>
                )}

                <div className="act-list-grid">
                    {filteredActivities.length > 0 ? (
                        filteredActivities.map((activity) => {
                            const intensityLabels = getIntensityLabels(activity);
                            const description = getCardDescription(activity);
                            const isFree = activity.isFree === 1 || activity.isFree === true;
                            const hasMinutes = activity.minutes !== null && activity.minutes !== undefined;

                            return (
                                <Link
                                    key={activity.idactivity}
                                    to={`/activities/${activity.idactivity}`}
                                    className="act-list-card"
                                >
                                    <div className="act-list-card-media">
                                        <i className="fas fa-running" aria-hidden="true" />
                                        {hasMinutes && (
                                            <span className="act-list-card-badge">{activity.minutes} min</span>
                                        )}
                                    </div>
                                    <div className="act-list-card-body">
                                        <h2>{activity.name}</h2>
                                        {description && (
                                            <p className="act-list-card-desc">{description}</p>
                                        )}
                                        <div className="act-list-card-meta">
                                            <span className={`act-list-pill ${isFree ? '' : 'act-list-pill--premium'}`}>
                                                <i className={`fas ${isFree ? 'fa-gift' : 'fa-star'}`} aria-hidden="true" />
                                                {isFree ? 'Gratis' : 'Premium'}
                                            </span>
                                            {hasMinutes && (
                                                <span className="act-list-pill">
                                                    <i className="fas fa-clock" aria-hidden="true" />
                                                    {activity.minutes} min
                                                </span>
                                            )}
                                            {activity.environment_name && (
                                                <span className="act-list-pill">
                                                    <i className="fas fa-mountain" aria-hidden="true" />
                                                    {activity.environment_name}
                                                </span>
                                            )}
                                            {intensityLabels.map((label) => (
                                                <span key={label} className="act-list-pill">
                                                    <i className="fas fa-bolt" aria-hidden="true" />
                                                    {label}
                                                </span>
                                            ))}
                                            {activity.programName && (
                                                <span className="act-list-pill">
                                                    <i className="fas fa-list-alt" aria-hidden="true" />
                                                    {activity.programName}
                                                </span>
                                            )}
                                            {activity.albums?.map((album) => (
                                                <span key={album.name} className="act-list-pill act-list-pill--album">
                                                    <i className="fas fa-compact-disc" aria-hidden="true" />
                                                    {album.name}
                                                </span>
                                            ))}
                                        </div>
                                        <span className="act-list-card-cta">
                                            Se aktivitet
                                            <i className="fas fa-arrow-right" aria-hidden="true" />
                                        </span>
                                    </div>
                                </Link>
                            );
                        })
                    ) : (
                        <p className="act-list-empty">
                            {searchTerm.trim()
                                ? 'Ingen aktiviteter funnet som matcher søket ditt'
                                : 'Ingen aktiviteter funnet'}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
